import React from "react";
import { getDataSourceType, isImageUrl, getItemThumbnail, getThumbnailHandler } from "./dataSourceAdapters";
// Remove the circular dependency import
// import {
//   AudioPreview,
//   AudioThumbnail,
//   DocumentPreview,
//   DocumentThumbnail,
//   ImagePreview,
//   ImageThumbnail,
//   MediaPlaceholder,
//   PDFPreview,
//   PDFThumbnail,
//   VideoPreview,
//   VideoThumbnail
// } from '../components/media/MediaComponents';
import { MediaResponse, MediaItemDetailed } from "../types";
import { apiFetch } from "./api";
import { FILE_EXTENSIONS } from "./mediaTypes";
// TEMPORARY: Import thumbnail construction utilities (remove when backend provides URLs)
import { constructThumbnailUrl } from "./temporaryUrlUtils";

export type MediaType = "image" | "pdf" | "audio" | "video" | "document" | "embed" | "unknown";

/**
 * Media information structure that consolidates all media properties
 */
export interface MediaInfo {
  mediaType: MediaType;
  url: string;
  thumbnailUrl: string | null;
  filename: string;
  isPdfFile: boolean;
  isVideoFile: boolean;
  isImageFile: boolean;
  needsFetch: boolean;
  // Remove the component property to break circular dependency
  // component: React.FC<any>;
}

/**
 * Comprehensive media information function - centralizes logic that's
 * currently scattered across multiple components
 */
export const getMediaInfo = (options: {
  item?: MediaResponse | MediaItemDetailed | null;
  url?: string;
  file?: File;
  filename?: string;
  mode?: "thumbnail" | "preview";
}): MediaInfo => {
  const { item, url: explicitUrl, file, filename: explicitFilename, mode = "preview" } = options;

  // Initialize result with defaults
  let mediaInfo: MediaInfo = {
    mediaType: "unknown",
    url: "",
    thumbnailUrl: null,
    filename: "",
    isPdfFile: false,
    isVideoFile: false,
    isImageFile: false,
    needsFetch: false
    // Remove the component property to break circular dependency
    // component: MediaPlaceholder
  };

  // 1. Determine final filename
  mediaInfo.filename = explicitFilename || file?.name || item?.original_file?.filename || item?.name || "";

  // 2. Determine media type
  let detectionSource: string | MediaResponse = "";

  if (file) {
    detectionSource = file.name;
  } else if (explicitUrl) {
    detectionSource = explicitUrl;
  } else if (item) {
    // Always use the full getMediaType function for comprehensive detection including fallbacks
    detectionSource = item as unknown as MediaResponse;
  }

  if (detectionSource) {
    mediaInfo.mediaType = getMediaType(detectionSource);
  }

  // 3. Set type flags based on media type
  mediaInfo.isPdfFile = mediaInfo.mediaType === "pdf";
  mediaInfo.isVideoFile = mediaInfo.mediaType === "video";
  mediaInfo.isImageFile = mediaInfo.mediaType === "image";

  // 4. Determine URL - FIXED ORDER TO PRIORITIZE MANAGED BLOB URLS
  // Check for explicit URL first
  if (explicitUrl) {
    mediaInfo.url = constructMediaUrl(explicitUrl);
  }
  // Then check for existing managed blob URL (previewUrl) to avoid creating duplicates
  else if (item && "previewUrl" in item && item.previewUrl) {
    console.log("Using existing managed previewUrl for item:", item.id, item.previewUrl);
    mediaInfo.url = item.previewUrl; // Already a blob URL, don't construct
  }
  // Only create new blob URL if no managed URL exists
  else if (file) {
    mediaInfo.url = URL.createObjectURL(file);
  }
  // Finally check item properties for server URLs
  else if (item) {
    // For videos, PDFs, and audio, always use the full URL
    if (mediaInfo.isVideoFile || mediaInfo.isPdfFile || (item.url && (isPdfUrl(item.url) || isVideoUrl(item.url)))) {
      // First try using the direct URL
      if (item.url) {
        mediaInfo.url = constructMediaUrl(item.url);
      }
      // If no direct URL, try remote_details
      else if (item.remote_details?.url) {
        mediaInfo.url = constructMediaUrl(item.remote_details.url);
      }
    } else {
      // For images and other content, use preview or thumbnail
      mediaInfo.url = constructMediaUrl(
        // Try all possible URL sources, prioritizing the most appropriate
        item.url || item.remote_details?.url || item.thumbnail || ""
      );
    }

    // Set thumbnail URL if available (for use as fallback)
    if (item.thumbnail) {
      mediaInfo.thumbnailUrl = constructMediaUrl(item.thumbnail);
    } else if (item.remote_details?.thumbnail) {
      mediaInfo.thumbnailUrl = constructMediaUrl(item.remote_details.thumbnail);
    }
  }

  // 5. Determine if we need to fetch the full item from the API
  if (
    item?.id &&
    (mediaInfo.isPdfFile ||
      mediaInfo.isVideoFile ||
      (!item.url && item.thumbnail) ||
      // Also fetch if the item looks like a non-image file (for better detection)
      (item.url && !mediaInfo.isImageFile && !explicitUrl))
  ) {
    mediaInfo.needsFetch = true;
  }

  // 6. Get the appropriate component
  // const config = getMediaConfigForType(mediaInfo.mediaType);
  // mediaInfo.component = mode === 'preview' ? config.previewComponent : config.thumbnailComponent;

  // 7. Special handling for PDFs with query parameters
  if (mediaInfo.isPdfFile && mediaInfo.url && mediaInfo.url.includes("?")) {
    mediaInfo.url = stripQueryParams(mediaInfo.url);
  }

  return mediaInfo;
};

/**
 * Helper function to strip query parameters from URLs
 * Used primarily for PDFs where query params can cause loading issues
 */
export const stripQueryParams = (url: string): string => {
  const queryIndex = url.indexOf("?");
  return queryIndex !== -1 ? url.substring(0, queryIndex) : url;
};

export interface MediaPreviewProps {
  url: string;
  title?: string;
  onClose?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  scale?: number;
  width?: string | number;
  height?: string | number;
}

export interface MediaThumbnailProps {
  url: string;
  title?: string;
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  width?: string | number;
  height?: string | number;
}

function getItemType(type: string) {
  // 🚨 FIX: Handle incorrect MIME types from server
  if (type === "model/vnd.mts") return "video";

  if (type.startsWith("embed/") || type === "embed") return "embed";
  if (type.includes("pdf") || type === "application/pdf") return "pdf";
  if (type === "photo" || type === "image" || type.startsWith("image/")) return "image";
  if (type === "video" || type.startsWith("video/")) return "video";
  if (type === "audio" || type.startsWith("audio/")) return "audio";
  if (type.includes("document") || type === "application/msword" || type.includes("officedocument")) return "document";
}

function detectMediaTypeFromUrl(baseUrl: string) {
  const normalized = baseUrl.split("?")[0].toLowerCase();

  const imagePattern = new RegExp(`\\.(${FILE_EXTENSIONS.IMAGE.join("|")})$`, "i");
  const audioPattern = new RegExp(`\\.(${FILE_EXTENSIONS.AUDIO.join("|")})$`, "i");
  const videoPattern = new RegExp(`\\.(${FILE_EXTENSIONS.VIDEO.join("|")})$`, "i");
  const documentPattern = new RegExp(`\\.(${FILE_EXTENSIONS.DOCUMENT.join("|")})$`, "i");

  if (imagePattern.test(normalized)) return "image";
  if (/\.pdf$/i.test(normalized)) return "pdf";
  if (audioPattern.test(normalized)) return "audio";
  if (videoPattern.test(normalized)) return "video";
  if (documentPattern.test(normalized)) return "document";

  return null;
}

// Get media type with better URL handling
export const getMediaType = (item: MediaResponse | string): MediaType => {
  // For server-returned MediaResponse objects
  if (typeof item !== "string" && item) {
    // 🚨 IMPORTANT: This function contains fixes for incorrect server MIME types
    // The server sometimes returns 'model/vnd.mts' for MTS video files, which is incorrect
    // The correct MIME type should be 'video/mp2t' or 'application/octet-stream'
    // We handle this by:
    // 1. Directly correcting known incorrect MIME types
    // 2. Adding incorrect MIME types to the "generic" list to trigger filename fallback
    // 3. Enhanced debugging to identify infinite loops caused by unrecognized types

    // First check media_type property
    if (item.media_type) {
      const type = item.media_type.toLowerCase();

      const mediaType = getItemType(type);
      if (mediaType) return mediaType;
    }

    // Then check MIME type from remote_details or original_file
    const mimeType = item.remote_details?.mime || item.original_file?.type;
    if (mimeType) {
      const mime = mimeType.toLowerCase();

      const mediaType = getItemType(mime);
      if (mediaType) return mediaType;
    }

    // IMPORTANT: Fallback to filename extension checking for generic/incorrect MIME types
    // This handles cases where server returns 'application/octet-stream' or incorrect MIME types for video files
    const isGenericMimeType =
      !item.media_type ||
      item.media_type === "application/octet-stream" ||
      item.media_type === "model/vnd.mts" || // Handle incorrect MTS MIME type
      (mimeType && (mimeType === "application/octet-stream" || mimeType === "binary/octet-stream" || mimeType === "model/vnd.mts")); // Handle incorrect MTS MIME type in remote_details

    if (isGenericMimeType) {
      // Try to get filename from various sources
      const filename = item.original_file?.filename || item.url || item.remote_details?.url || "";

      if (filename) {
        // Use the same extension checking logic as for strings
        const baseUrl = filename.split("?")[0].toLowerCase();

        const detectType = detectMediaTypeFromUrl(baseUrl);
        if (detectType) return detectType;
      }
    }
  }

  // For strings (filenames/URLs) only check extension for local files
  if (typeof item === "string") {
    // Extract the base filename without query parameters
    const baseUrl = item.split("?")[0].toLowerCase();

    const detectType = detectMediaTypeFromUrl(baseUrl);
    if (detectType) return detectType;
  }

  // Check from properties
  if (typeof item !== "string" && "properties" in item && item?.properties) {
    const properties = item.properties as Record<string, string>;

    // check for filename or url
    const filename = properties?.filename || properties.url || "";
    if (filename) {
      const baseUrl = filename?.split("?")[0].toLowerCase();
      const detectType = detectMediaTypeFromUrl(baseUrl);
      if (detectType) return detectType;
    }

    // Check from media_type
    if (properties?.media_type) {
      const mediaType = getItemType(properties?.media_type);
      if (mediaType) return mediaType;
    }
  }

  // Detect embed from remote_details even when media_type wasn't set
  // (backwards compatible with backends that don't expose embed_url as a top-level field)
  if (typeof item !== "string" && item) {
    const rd = (item as any).remote_details;
    if ((item as any).embed_url || (rd && rd.embed_url)) {
      return "embed";
    }
  }

  return "unknown";
};

// Normalize URLs for media display
export const constructMediaUrl = (url: string): string => {
  if (!url) return "";

  // If it's already a blob URL or data URL, return as is
  if (url.startsWith("blob:") || url.startsWith("data:")) {
    return url;
  }

  // If it's already an absolute URL, return as is
  if (url.startsWith("http:") || url.startsWith("https:")) {
    return url;
  }

  // Otherwise, construct a URL relative to the current origin
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Get media configuration based on file type
export const getMediaConfig = (filenameOrUrl: string): MediaType => {
  const mediaType = getMediaType(filenameOrUrl);
  return mediaType;
};

/**
 * Checks if a URL is absolute
 * @param url The URL to check
 * @returns boolean indicating if the URL is absolute
 */
export function isAbsoluteUrl(url: string): boolean {
  if (!url) return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

// Helper function to check if a file is previewable
export function isPreviewable(filename: string): boolean {
  const mediaType = getMediaType(filename);
  return mediaType !== "unknown";
}

// Helper function to get the appropriate thumbnail icon
export function getThumbnailIcon(filename: string): string {
  const mediaType = getMediaType(filename);
  switch (mediaType) {
    case "pdf":
      return "/icons/doc-icon.svg";
    case "video":
      return "/icons/play-button.png";
    case "audio":
      return "/icons/volume.png";
    case "document":
      return "/icons/doc-icon.svg";
    case "embed":
      return "/icons/no-image.png";
    default:
      return "/icons/no-image.png";
  }
}

export function isImageMediaItem(item: MediaResponse | null | undefined): boolean {
  if (!item) return false;
  return getMediaType(item.original_file?.filename || "") === "image";
}

/**
 * Gets the thumbnail URL for a media item by its ID
 * @param mediaId The ID of the media item
 * @returns The constructed thumbnail URL
 */
export function getMediaThumbnailUrl(mediaId: string | number): string {
  if (!mediaId) return "";

  const apiRoot = process.env.REACT_APP_API_ROOT?.replace(/\/$/, "");
  if (!apiRoot) {
    console.warn("REACT_APP_API_ROOT is not defined in environment variables");
    return "";
  }

  // Construct the thumbnail URL based on our API structure
  return constructMediaUrl(`/media/${mediaId}/thumbnail/`);
}

/**
 * Fetches a media item and returns its thumbnail URL
 * @param mediaId The ID of the media item
 * @returns Promise with the thumbnail URL
 */
export async function getMediaThumbnail(mediaId: string | number): Promise<string> {
  try {
    const response: MediaResponse = await apiFetch(`/media/${mediaId}/`);
    return response.thumbnail || "";
  } catch (error) {
    console.error("Error fetching media thumbnail:", error);
    return "";
  }
}

// Function to get a signed URL for media access
export const getSignedMediaUrl = async (originalUrl: string, mediaType: string): Promise<string> => {
  try {
    // If it's not a video, just return the original URL
    if (!mediaType?.includes("video") && !mediaType?.includes("mov") && !mediaType?.includes("mp4")) {
      return originalUrl;
    }

    // For videos, we need to get a signed URL from the server
    // First, extract the path to the media from the original URL
    const mediaPath = originalUrl.split("/").slice(3).join("/");

    // Check if we already have a signed URL for this path in localStorage
    const cachedUrl = localStorage.getItem(`signed_url:${mediaPath}`);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Request a signed URL from the server
    // This assumes there's an API endpoint for this purpose
    const apiRoot = process.env.REACT_APP_API_ROOT || "";
    const response = await fetch(`${apiRoot}/api/signed-media-url/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        media_path: mediaPath,
        media_type: mediaType
      }),
      credentials: "include"
    });

    if (!response.ok) {
      console.error("Failed to get signed URL:", response.statusText);
      return originalUrl;
    }

    const data = await response.json();
    if (data.signed_url) {
      // Cache the signed URL in localStorage for 15 minutes
      localStorage.setItem(`signed_url:${mediaPath}`, data.signed_url);
      setTimeout(
        () => {
          localStorage.removeItem(`signed_url:${mediaPath}`);
        },
        15 * 60 * 1000
      );

      return data.signed_url;
    }

    return originalUrl;
  } catch (error) {
    console.error("Error getting signed URL:", error);
    return originalUrl;
  }
};

// New utility function to get appropriate media URL
export const getMediaUrl = (item: MediaResponse | MediaItemDetailed | null, size: "thumbnail" | "preview" | "full" = "full"): string => {
  if (!item) return "";

  // Future implementation with URL parameters
  if (process.env.REACT_APP_USE_CDN_PARAMS === "true") {
    const baseUrl = item.url || "";
    // Don't add parameters to already-parametrized URLs
    if (baseUrl.includes("?")) return baseUrl;
    return `${baseUrl}?size=${size}`;
  }

  // Current implementation
  switch (size) {
    case "thumbnail":
      return item.thumbnail || item.url || "";
    case "preview":
      // For videos and PDFs, use full URL for preview
      if (item.media_type === "video" || item.media_type === "pdf") {
        return item.url || "";
      }
      // Only check previewUrl if it exists (not in all types)
      return (
        // Use optional chaining to safely access preview_url if it exists
        // @ts-ignore - Some item types might have previewUrl but not in type definition
        item.previewUrl || item.preview_url || item.thumbnail || item.url || ""
      );
    case "full":
    default:
      return item.url || "";
  }
};

// Helper to detect if a URL is a video
export const isVideoUrl = (url: string | null): boolean => {
  if (!url) return false;
  const lowercaseUrl = url.toLowerCase();
  return FILE_EXTENSIONS.VIDEO.some((ext) => lowercaseUrl.endsWith(`.${ext.toLowerCase()}`));
};

// Helper to detect if a URL is a PDF
export const isPdfUrl = (url: string | null): boolean => {
  if (!url) return false;
  return url.toLowerCase().endsWith(".pdf");
};

// TEMPORARY: constructCdnThumbnailUrl function moved to temporaryUrlUtils.ts
// This comment marks where the function was removed from this file

/**
 * Check if an item contains Fulcrum photo data
 */

/**
 * Check if an item is from a specific data source type
 * More readable than direct string comparisons
 */
export const isDataSourceType = (item: any, sourceType: string): boolean => {
  return getDataSourceType(item) === sourceType;
};

/**
 * Get thumbnail URL for an item based on its data source type
 * This function delegates to data source-specific handlers in the adapters
 */
const getThumbnailUrlByDataSource = (item: any, dataSourceType: string): string | undefined => {
  // Get the appropriate handler for this data source type
  const handler = getThumbnailHandler(dataSourceType);

  // Delegate to the data source-specific handler
  const result = handler(item);
  return result || undefined; // Convert null to undefined
};

// TEMPORARY: Get a safe thumbnail URL that ensures we only use actual image URLs as thumbnails
// TODO: Remove this when backend provides thumbnail URLs directly
export const getSafeThumbnailUrl = (item: any, endpoint?: any): string | undefined => {
  if (!item) return undefined;

  // Use the new data-source-specific adapter
  const result = getItemThumbnail(item, endpoint);

  // Clean implementation using data-source-specific adapters

  return result || undefined;
};

// Get appropriate URL based on media type and context
export const getContextAppropriateUrl = (item: any, context: "thumbnail" | "preview" | "full" = "thumbnail"): string | null => {
  if (!item) return null;

  const rawUrl = context === "thumbnail" ? item.thumbnail || item.primary_image?.thumbnail : item.url || item.primary_image?.url;

  if (!rawUrl) return null;

  // For thumbnails, only return image URLs
  if (context === "thumbnail" && !isImageUrl(rawUrl)) {
    return null;
  }

  return constructMediaUrl(rawUrl);
};

// Add a helper function at the end of the file to detect type from string
/**
 * Helper to get media type from a string like 'pdf', 'image', etc.
 */
export const getMediaTypeFromString = (typeString: string): MediaType => {
  const type = typeString.toLowerCase();
  if (type.startsWith("embed/") || type === "embed") return "embed";
  if (type.includes("pdf") || type === "application/pdf") return "pdf";
  if (type === "photo" || type === "image" || type.startsWith("image/")) return "image";
  if (type === "video" || type.startsWith("video/")) return "video";
  if (type === "audio" || type.startsWith("audio/")) return "audio";
  if (type === "document") return "document";
  return "unknown";
};

/**
 * Helper to detect if a URL is an audio file
 */
export const isAudioUrl = (url: string | null): boolean => {
  if (!url) return false;
  const lowercaseUrl = url.toLowerCase();
  return FILE_EXTENSIONS.AUDIO.some((ext) => lowercaseUrl.endsWith(`.${ext.toLowerCase()}`)) || lowercaseUrl.includes("audio/");
};
